/*
 *
 * Profile reducer
 *
 */

import produce from 'immer';

import Platform from '../../../utils/Platform';
import { CHANGE_SELECTED_LOAN_SUCCESS, RESET_USER_STATE } from '../../Main/constants';
import {
  CHANGE_EMAIL,
  CHANGE_EMAIL_FAILURE,
  CHANGE_EMAIL_SUCCESS,
  CHANGE_EMAIL_VALIDATION_FAILURE,
  CHANGE_INPUT,
  CHANGE_INPUT_ERROR,
  CHANGE_MOBILE_NUMBER,
  CHANGE_MOBILE_NUMBER_FAILURE,
  CHANGE_MOBILE_NUMBER_SUCCESS,
  CHANGE_MOBILE_NUMBER_VALIDATION_FAILURE,
  CHANGE_PASSWORD,
  CHANGE_PASSWORD_FAILURE,
  CHANGE_PASSWORD_SUCCESS,
  CHANGE_PASSWORD_VALIDATION_FAILURE,
  CHANGE_STEP,
  CHOOSE_DOCUMENT,
  CHOOSE_DOCUMENT_CANCELLATION,
  CHOOSE_DOCUMENT_FAILURE,
  CHOOSE_DOCUMENT_SUCCESS,
  CLOSE_MODAL_STATE,
  FETCH_LEGAL_NAME_MILITARY_STATUS,
  FETCH_LEGAL_NAME_MILITARY_STATUS_FAILURE,
  FETCH_LEGAL_NAME_MILITARY_STATUS_SUCCESS,
  FETCH_MOBILE_NUMBER_STATUS,
  FETCH_MOBILE_NUMBER_STATUS_FAILURE,
  FETCH_MOBILE_NUMBER_STATUS_SUCCESS,
  FETCH_PROFILE,
  FETCH_PROFILE_FAILURE,
  FETCH_PROFILE_SUCCESS,
  INPUT_ERRORS,
  OPEN_MODAL_STATE,
  PROCESS_DOCUMENT,
  PROCESS_DOCUMENT_FAILURE,
  PROCESS_DOCUMENT_SUCCESS,
  RESET_BANNER_ERROR,
  RESET_ERRORS_STATE,
  RESET_FORM_ERRORS_STATE,
  RESET_FORM_STATE,
  RESET_REQUEST_STATUS_STATE,
  RESET_STEP_STATE,
  UPLOAD_DOCUMENT,
  UPLOAD_DOCUMENT_FAILURE,
  UPLOAD_DOCUMENT_SUCCESS,
  VERIFY_MOBILE_NUMBER,
  VERIFY_MOBILE_NUMBER_FAILURE,
  VERIFY_MOBILE_NUMBER_SUCCESS,
  VERIFY_MOBILE_NUMBER_VALIDATION_FAILURE,
} from './constants';

export const initialState = {
  currentSetting: {},
  errors: {
    bannerError: false,
    serviceErrors: {
      fetchLegalNameMilitaryStatus: false,
      fetchMobileNumberStatus: false,
      fetchProfile: false,
    },
  },
  formConfirmations: {
    email: '',
    mobileNumber: '',
  },
  formErrors: {
    email: {
      confirmEmail: '',
      newEmail: '',
    },
    mobileNumber: {
      confirmMobileNumber: '',
      newMobileNumber: '',
    },
    password: {
      confirmNewPassword: '',
      newPassword: '',
      oldPassword: '',
    },
    uploadDocument: { fileInput: '' },
    verifyMobileNumber: { passCode: '' },
  },
  forms: {
    email: {
      confirmEmail: '',
      newEmail: '',
    },
    mobileNumber: {
      confirmMobileNumber: '',
      newMobileNumber: '',
    },
    password: {
      confirmNewPassword: '',
      newPassword: '',
      oldPassword: '',
    },
    uploadDocument: {
      fileInput: {
        fileData: '',
        fileName: '',
        fileSize: 0,
        fileType: '',
      },
    },
    verifyMobileNumber: { passCode: '' },
  },
  isModalOpen: false,
  legalName: '',
  militaryServiceStatus: '',
  mobileNumberStatus: {},
  mobileNumberVerifyToken: '',
  modalState: '',
  requestStatus: {
    chooseDocument: 'idle',
    email: 'idle',
    fetchLegalNameMilitaryStatus: 'idle',
    fetchMobileNumberStatus: 'idle',
    fetchProfile: 'idle',
    mobileNumber: 'idle',
    password: 'idle',
    processDocument: 'idle',
    uploadDocument: 'idle',
    verifyMobileNumber: 'idle',
  },
  step: 1,
};

/* eslint-disable consistent-return, default-case, no-param-reassign */
const profileReducer = produce((draft, { payload, type }) => {
  switch (type) {
    case CHANGE_INPUT: {
      const { field, form, value } = payload;
      draft.forms[form][field] = value;
      break;
    }
    case CHANGE_INPUT_ERROR: {
      const {
        field,
        form,
        nestedField,
        value,
      } = payload;
      if (nestedField) {
        draft.formErrors[form][field][nestedField] = value;
      }
      draft.formErrors[form][field] = value;
      break;
    }
    case CHANGE_EMAIL: {
      draft.errors.serviceErrors.email = initialState.errors.serviceErrors.email;
      draft.requestStatus.email = 'loading';
      break;
    }
    case CHANGE_EMAIL_FAILURE: {
      const { error } = payload;
      draft.errors.bannerError = error;
      draft.requestStatus.email = 'failed';
      break;
    }
    case CHANGE_EMAIL_SUCCESS: {
      const { currentSetting } = payload;
      draft.currentSetting = currentSetting;
      draft.requestStatus.email = 'succeeded';
      break;
    }
    case CHANGE_EMAIL_VALIDATION_FAILURE: {
      const { error } = payload;
      draft.formErrors.email.newEmail = error;
      draft.requestStatus.email = 'failed';
      break;
    }
    case CHANGE_MOBILE_NUMBER: {
      draft.errors.bannerError = initialState.errors.bannerError;
      draft.formConfirmations.mobileNumber = initialState.formConfirmations.mobileNumber;
      draft.requestStatus.mobileNumber = 'loading';
      break;
    }
    case CHANGE_MOBILE_NUMBER_FAILURE: {
      const { error } = payload;
      draft.errors.bannerError = error;
      draft.requestStatus.mobileNumber = 'failed';
      break;
    }
    case CHANGE_MOBILE_NUMBER_SUCCESS: {
      const {
        currentSetting,
        message,
      } = payload;
      const { allowedOperation, status, token } = currentSetting;
      draft.formConfirmations.mobileNumber = message;
      draft.mobileNumberStatus.allowedOperation = allowedOperation;
      draft.mobileNumberStatus.status = status;
      draft.mobileNumberVerifyToken = token;
      draft.requestStatus.mobileNumber = 'succeeded';
      break;
    }
    case CHANGE_MOBILE_NUMBER_VALIDATION_FAILURE: {
      const { error } = payload;
      draft.formErrors.mobileNumber.newMobileNumber = error;
      draft.requestStatus.mobileNumber = 'failed';
      break;
    }
    case CHANGE_PASSWORD: {
      draft.errors.serviceErrors.password = initialState.errors.serviceErrors.password;
      draft.formErrors.password = initialState.formErrors.password;
      draft.requestStatus.password = 'loading';
      break;
    }
    case CHANGE_PASSWORD_FAILURE: {
      draft.requestStatus.password = 'failed';
      break;
    }
    case CHANGE_PASSWORD_SUCCESS: {
      draft.requestStatus.password = 'succeeded';
      break;
    }
    case CHANGE_PASSWORD_VALIDATION_FAILURE: {
      const { field, message } = payload;
      draft.formErrors.password[field] = message;
      draft.requestStatus.password = 'failed';
      break;
    }
    case CHANGE_SELECTED_LOAN_SUCCESS: {
      return initialState;
    }
    case CHANGE_STEP: {
      const { step } = payload;
      draft.step = step;
      break;
    }
    case CHOOSE_DOCUMENT: {
      /**
       * Web isn't able to tell us if user cancels from choosing a file, so if we set it to
       * 'loading' here and they cancel, it would remain loading even after modal closes.
       * But, since we can safely show (and cancel) loading state on native, it is helpful to do so
       * since sometimes it's an awkwardly long moment between the file picker modal closing
       * and the system actually telling us the process is finished.
       *
       * UPDATE: Actually there is a bug on iOS where if a user swipes down to close the ImagePicker
       * drawer, it never returns as 'cancelled'. This bug used to exist on DocumentPicker but is
       * fixed in our current SDK 41 (see https://github.com/expo/expo/pull/10325). There is a fix
       * for it on ImagePicker, but it's only available as of SDK 44 (see
       * https://github.com/expo/expo/pull/15511). So for now, I am commenting out the'loading'
       * state, and we can uncomment it when we upgrade to SDK 44.
       */
      if (Platform.OS === 'ios' || Platform.OS === 'android') {
        // draft.requestStatus.chooseDocument = 'loading';
        draft.requestStatus.chooseDocument = 'idle';
      } else {
        draft.requestStatus.chooseDocument = 'idle';
      }
      break;
    }
    case CHOOSE_DOCUMENT_CANCELLATION: {
      draft.requestStatus.chooseDocument = 'idle';
      break;
    }
    case CHOOSE_DOCUMENT_FAILURE: {
      const { bannerError } = payload;
      draft.errors.bannerError = bannerError;
      draft.forms.uploadDocument.fileInput = initialState.forms.uploadDocument.fileInput;
      draft.requestStatus.chooseDocument = 'failed';
      break;
    }
    case CHOOSE_DOCUMENT_SUCCESS: {
      draft.requestStatus.chooseDocument = 'succeeded';
      break;
    }
    case CLOSE_MODAL_STATE: {
      draft.isModalOpen = initialState.isModalOpen;
      draft.modalState = initialState.modalState;
      break;
    }
    case FETCH_LEGAL_NAME_MILITARY_STATUS: {
      draft.errors.serviceErrors.fetchLegalNameMilitaryStatus = false;
      draft.requestStatus.fetchLegalNameMilitaryStatus = 'loading';
      break;
    }
    case FETCH_LEGAL_NAME_MILITARY_STATUS_FAILURE: {
      const { error } = payload;
      draft.errors.serviceErrors.fetchLegalNameMilitaryStatus = error;
      draft.requestStatus.fetchLegalNameMilitaryStatus = 'failed';
      break;
    }
    case FETCH_LEGAL_NAME_MILITARY_STATUS_SUCCESS: {
      const { legalName, militaryServiceStatus } = payload;
      draft.legalName = legalName;
      draft.militaryServiceStatus = militaryServiceStatus;
      draft.requestStatus.fetchLegalNameMilitaryStatus = 'succeeded';
      break;
    }
    case FETCH_MOBILE_NUMBER_STATUS: {
      draft.errors.serviceErrors.fetchMobileNumberStatus =
        initialState.errors.serviceErrors.fetchMobileNumberStatus;
      draft.requestStatus.fetchMobileNumberStatus = 'loading';
      break;
    }
    case FETCH_MOBILE_NUMBER_STATUS_FAILURE: {
      const { error } = payload;
      draft.errors.serviceErrors.fetchMobileNumberStatus = error;
      draft.requestStatus.fetchMobileNumberStatus = 'failed';
      break;
    }
    case FETCH_MOBILE_NUMBER_STATUS_SUCCESS: {
      const { allowedOperation, currentSetting, status } = payload;
      if (currentSetting) {
        const { mobileNumber } = currentSetting;
        draft.currentSetting.mobileNumber = mobileNumber;
      }
      draft.mobileNumberStatus.allowedOperation = allowedOperation;
      draft.mobileNumberStatus.status = status;
      draft.requestStatus.fetchMobileNumberStatus = 'succeeded';
      break;
    }
    case FETCH_PROFILE: {
      draft.errors.serviceErrors.fetchProfile = initialState.errors.serviceErrors.fetchProfile;
      draft.requestStatus.fetchProfile = 'loading';
      break;
    }
    case FETCH_PROFILE_FAILURE: {
      const { error } = payload;
      draft.errors.serviceErrors.fetchProfile = error;
      draft.requestStatus.fetchProfile = 'failed';
      break;
    }
    case FETCH_PROFILE_SUCCESS: {
      const { currentSetting } = payload;
      draft.currentSetting = currentSetting;
      draft.requestStatus.fetchProfile = 'succeeded';
      break;
    }
    case INPUT_ERRORS: {
      const { form, formErrors } = payload;
      const fields = Object.keys(formErrors);
      // eslint-disable-next-line no-return-assign
      fields.forEach((field) => draft.formErrors[form][field] = formErrors[field].message || '');
      break;
    }
    case PROCESS_DOCUMENT: {
      const { file: { name } } = payload;
      draft.requestStatus.processDocument = 'loading';
      draft.errors.bannerError = initialState.errors.bannerError;
      draft.formErrors.uploadDocument = initialState.formErrors.uploadDocument;
      draft.forms.uploadDocument.fileInput = {
        ...initialState.forms.uploadDocument.fileInput,
        fileName: name,
      };
      break;
    }
    case PROCESS_DOCUMENT_FAILURE: {
      const {
        bannerError,
        file,
        validationError,
      } = payload;
      draft.requestStatus.processDocument = 'failed';
      if (bannerError) {
        draft.errors.bannerError = bannerError;
      }
      if (file && validationError) {
        const {
          data: fileData,
          name: fileName,
          size: fileSize,
          type: fileType,
        } = file;
        draft.formErrors.uploadDocument.fileInput = validationError;
        draft.forms.uploadDocument.fileInput.fileData = fileData;
        draft.forms.uploadDocument.fileInput.fileName = fileName;
        draft.forms.uploadDocument.fileInput.fileSize = fileSize;
        // fileType not avaiable on mobile
        if (fileType) {
          draft.forms.uploadDocument.fileInput.fileType = fileType;
        }
      }
      break;
    }
    case PROCESS_DOCUMENT_SUCCESS: {
      const {
        file: {
          data: fileData,
          name: fileName,
          size: fileSize,
          type: fileType,
        },
      } = payload;
      draft.requestStatus.processDocument = 'succeeded';
      draft.forms.uploadDocument.fileInput.fileData = fileData;
      draft.forms.uploadDocument.fileInput.fileName = fileName;
      draft.forms.uploadDocument.fileInput.fileSize = fileSize;
      // Type not avaiable on mobile
      if (fileType) {
        draft.forms.uploadDocument.fileInput.fileType = fileType;
      }
      break;
    }
    case RESET_BANNER_ERROR: {
      draft.errors.bannerError = initialState.errors.bannerError;
      break;
    }
    case OPEN_MODAL_STATE: {
      const { modalState } = payload;
      draft.isModalOpen = true;
      draft.modalState = modalState;
      break;
    }
    case RESET_ERRORS_STATE: {
      draft.errors = initialState.errors;
      break;
    }
    case RESET_FORM_ERRORS_STATE: {
      const { field, form } = payload;
      draft.formErrors[form][field] = initialState.formErrors[form][field];
      break;
    }
    case RESET_FORM_STATE: {
      const { form } = payload;
      draft.errors = initialState.errors;
      draft.formErrors[form] = initialState.formErrors[form];
      draft.forms[form] = initialState.forms[form];
      break;
    }
    case RESET_REQUEST_STATUS_STATE: {
      const { state } = payload;
      draft.requestStatus[state] = initialState.requestStatus[state];
      break;
    }
    case RESET_STEP_STATE: {
      draft.step = initialState.step;
      break;
    }
    case RESET_USER_STATE: {
      return initialState;
    }
    case UPLOAD_DOCUMENT: {
      draft.requestStatus.uploadDocument = 'loading';
      draft.errors.bannerError = initialState.errors.bannerError;
      draft.formErrors.uploadDocument = initialState.formErrors.uploadDocument;
      break;
    }
    case UPLOAD_DOCUMENT_FAILURE: {
      const { bannerError, isMobile, validationError } = payload;
      const hasPartialError = !!bannerError || !!validationError;
      draft.requestStatus.uploadDocument = 'failed';
      if (hasPartialError) {
        if (bannerError) {
          draft.errors.bannerError = bannerError;
        }
        if (validationError) {
          draft.formErrors.uploadDocument.fileInput = validationError;
        }
      } else if (!isMobile) {
        // Desktop full error navigation handled here. Mobile is handled in saga.
        draft.step += 2;
      }
      break;
    }
    case UPLOAD_DOCUMENT_SUCCESS: {
      const { isMobile } = payload;
      draft.requestStatus.uploadDocument = 'succeeded';
      if (!isMobile) {
        draft.step += 1;
      }
      break;
    }
    case VERIFY_MOBILE_NUMBER: {
      draft.errors.bannerError = initialState.errors.bannerError;
      draft.formErrors.verifyMobileNumber = initialState.formErrors.verifyMobileNumber;
      draft.formConfirmations.mobileNumber = initialState.formConfirmations.mobileNumber;
      draft.requestStatus.verifyMobileNumber = 'loading';
      break;
    }
    case VERIFY_MOBILE_NUMBER_FAILURE: {
      const { error } = payload;
      draft.errors.bannerError = error;
      draft.requestStatus.verifyMobileNumber = 'failed';
      break;
    }
    case VERIFY_MOBILE_NUMBER_SUCCESS: {
      const { currentSetting, message } = payload;
      draft.currentSetting = currentSetting;
      draft.formConfirmations.mobileNumber = message;
      draft.requestStatus.verifyMobileNumber = 'succeeded';
      break;
    }
    case VERIFY_MOBILE_NUMBER_VALIDATION_FAILURE: {
      const { error } = payload;
      draft.formErrors.verifyMobileNumber.passCode = error;
      draft.requestStatus.verifyMobileNumber = 'failed';
      break;
    }
  }
}, initialState);

export default profileReducer;
